import React, { useState } from 'react';
import axios from 'axios';

import { useNavigate } from 'react-router-dom';
import {
  Container, TextField, Button, Typography, Box, MenuItem, Select,
  FormControl, InputLabel, styled, Divider, Paper,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  CircularProgress, Checkbox, FormControlLabel, FormGroup
} from '@mui/material';

import Sidebar from '../components/Sidebar';
import config from '../config';

const PageContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  alignContent: 'center',
  width: '100%',
  height: '100%',
});

const ContentContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  alignContent: 'center',
  width: '100%',
  paddingBottom: '2rem',
});

const RerenderPage = () => {
  const navigate = useNavigate();
  const [productId, setProductId] = useState('');
  const [size, setSize] = useState('');
  const [rerenderType, setRerenderType] = useState(11); // Default to 'clip'
  const [status, setStatus] = useState('');

  // State для дополнительного функционала
  const [imgGenProductIds, setImgGenProductIds] = useState('');
  const [imgGenStatus, setImgGenStatus] = useState('');
  const [openDialog, setOpenDialog] = useState(false);

  // States for Calculate Mask Threshold
  const [calcProductId, setCalcProductId] = useState('');
  const [calcContextMode, setCalcContextMode] = useState('');
  const [calcSize, setCalcSize] = useState(55);
  const [calcMaskThreshold, setCalcMaskThreshold] = useState('');
  const [calcImages, setCalcImages] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcMessage, setCalcMessage] = useState('');

  // States for CDN Purge
  const [purgeProductIds, setPurgeProductIds] = useState('');
  const [purgeStatus, setPurgeStatus] = useState('');
  const [isPurging, setIsPurging] = useState(false);

  // States for Lock VTO/POM
  const [lockProductIds, setLockProductIds] = useState('');
  const [lockVto, setLockVto] = useState(false);
  const [lockPom, setLockPom] = useState(false);
  const [lockResults, setLockResults] = useState(null);
  const [lockError, setLockError] = useState('');
  const [isLocking, setIsLocking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const params = {
        product_id: productId, // Send as string
        size: size ? parseInt(size, 10) : null,
        rerender_type: rerenderType,
      };

      const response = await axios.put(`${config.serverUrl}/rerender`, null, {
        params: params
      });

      setStatus(response.data.status);
    } catch (error) {
      setStatus('Error: ' + error.message);
    }
  };

  // Обработчики диалогового окна
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  // Обработчик новой API
  const handleImageGeneration = async () => {
    handleCloseDialog();
    setImgGenStatus('Sending a request...');

    try {
      const response = await axios.post(`${config.serverUrl}/image-generation-request`, {
        product_ids: imgGenProductIds
      });
      setImgGenStatus(`Success: IDs processed - ${response.data.processed_ids.join(', ')}`);
      setImgGenProductIds('');
    } catch (error) {
      setImgGenStatus(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Handlers for Calculate Mask Threshold
  const handleGetImages = async () => {
    if (!calcProductId || !calcContextMode) {
      setCalcMessage('Please fill in Product ID and Context Mode ID to get images.');
      return;
    }

    setCalcMessage('Fetching images...');
    setCalcImages([]);

    try {
      const response = await axios.get(`${config.serverUrl}/render/postprocess-images`, {
        params: {
          product_id: parseInt(calcProductId, 10),
          context_mode_id: parseInt(calcContextMode, 10)
        }
      });
      setCalcImages(response.data);
      setCalcMessage('');
    } catch (error) {
      setCalcMessage(`Error getting images: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleCalculateThreshold = async () => {
    if (!calcProductId || !calcContextMode || !calcMaskThreshold) {
      setCalcMessage('Please fill in Product ID, Context Mode ID, and Mask Threshold.');
      return;
    }

    setIsCalculating(true);
    setCalcMessage('Processing... Please wait.');
    setCalcImages([]); // Optionally clear old images

    try {
      await axios.post(`${config.serverUrl}/render/calculate-threshold`, null, {
        params: {
          product_id: parseInt(calcProductId, 10),
          context_mode_id: parseInt(calcContextMode, 10),
          size: calcSize,
          mask_threshold: parseInt(calcMaskThreshold, 10)
        }
      });

      setCalcMessage('Calculation successful! You can now view the new images by clicking "Get Images".');
    } catch (error) {
      setCalcMessage(`Error calculating threshold: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsCalculating(false);
    }
  };

  // Handler for CDN Purge
  const handlePurgeCdn = async () => {
    if (!purgeProductIds.trim()) {
      setPurgeStatus('Please enter product IDs.');
      return;
    }

    // Parse string into array of integers
    const idArray = purgeProductIds
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== '')
      .map(id => parseInt(id, 10));

    // Validate parsing
    if (idArray.some(isNaN)) {
      setPurgeStatus('Error: All IDs must be valid numbers.');
      return;
    }

    // Validate length constraint matching FastAPI model
    if (idArray.length === 0 || idArray.length > 70) {
      setPurgeStatus(`Error: Please provide between 1 and 70 product IDs. You provided ${idArray.length}.`);
      return;
    }

    setIsPurging(true);
    setPurgeStatus('Purging CDN...');

    try {
      await axios.post(`${config.serverUrl}/cdn/purge`, {
        product_ids: idArray
      });

      setPurgeStatus('Success: CDN purge request submitted.');
      setPurgeProductIds('');
    } catch (error) {
      setPurgeStatus(`Error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsPurging(false);
    }
  };

  // Handler for Lock VTO/POM
  const handleLockSubmit = async () => {
    const idArray = lockProductIds
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== '')
      .map(id => parseInt(id, 10));

    if (idArray.length === 0 || idArray.some(isNaN)) {
      setLockError('Please enter one or more valid product IDs, separated by commas.');
      setLockResults(null);
      return;
    }

    setLockError('');
    setIsLocking(true);
    setLockResults(null);

    try {
      const response = await axios.post(`${config.serverUrl}/models/lock`, {
        product_ids: idArray,
        lock_vto: lockVto,
        lock_pom: lockPom,
      });
      setLockResults(response.data);
    } catch (error) {
      setLockError(error.response?.data?.detail || error.message);
    } finally {
      setIsLocking(false);
    }
  };

  return (
    <PageContainer>
      <Sidebar navigate={navigate} />
      <ContentContainer>
        <Container maxWidth="sm">
          <Box sx={{ mt: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Rerender Product
            </Typography>
            <form onSubmit={handleSubmit}>
              <TextField
                label="Product IDs (comma separated)"
                type="text"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                fullWidth
                required
                margin="normal"
                placeholder="123, 456, 789"
                helperText="Enter product IDs separated by commas"
              />
              <TextField
                label="Size"
                type="number"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Rerender Type</InputLabel>
                <Select
                  value={rerenderType}
                  onChange={(e) => setRerenderType(e.target.value)}
                >
                  <MenuItem value={11}>Clip</MenuItem>
                  <MenuItem value={12}>Premium On Mode</MenuItem>
                  <MenuItem value={13}>Video Content</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" color="primary" fullWidth>
                  Rerender
                </Button>
              </Box>
            </form>
            {status && (
              <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
                {status}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 5 }} />

          {/* Re-generate projections */}
          <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom color="secondary.main">
              Re-Generate projections
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Functionality for mass requests for image regeneration.
            </Typography>

            <TextField
              label="Product IDs (comma separated)"
              type="text"
              value={imgGenProductIds}
              onChange={(e) => setImgGenProductIds(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="101, 102, 103"
            />

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={handleOpenDialog}
                disabled={!imgGenProductIds.trim()}
              >
                Send Request
              </Button>
            </Box>

            {imgGenStatus && (
              <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
                {imgGenStatus}
              </Typography>
            )}
          </Paper>

          {/* Calculate Mask Threshold */}
          <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom color="primary.main">
              Calculate Mask Threshold
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Adjust mask threshold and fetch post-processed images.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label="Product ID"
                type="number"
                value={calcProductId}
                onChange={(e) => setCalcProductId(e.target.value)}
                fullWidth
              />
              <TextField
                label="Context Mode ID"
                type="number"
                value={calcContextMode}
                onChange={(e) => setCalcContextMode(e.target.value)}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Size</InputLabel>
                <Select
                  value={calcSize}
                  label="Size"
                  onChange={(e) => setCalcSize(e.target.value)}
                >
                  <MenuItem value={55}>55</MenuItem>
                  <MenuItem value={65}>65</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Mask Threshold"
                type="number"
                value={calcMaskThreshold}
                onChange={(e) => setCalcMaskThreshold(e.target.value)}
                fullWidth
                InputProps={{ inputProps: { min: 30, max: 225 } }}
                helperText="Values between 30 and 225"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={handleGetImages}
                disabled={isCalculating || !calcProductId || !calcContextMode}
              >
                Get Images
              </Button>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleCalculateThreshold}
                disabled={isCalculating || !calcProductId || !calcContextMode || !calcMaskThreshold}
                startIcon={isCalculating ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </Button>
            </Box>

            {calcMessage && (
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  fontWeight: 'bold',
                  color: calcMessage.includes('Error') ? 'error.main' : 'success.main'
                }}
              >
                {calcMessage}
              </Typography>
            )}

            {/* Images Display Area */}
            {calcImages.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Post-processed Images:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {calcImages.map((imgUrl, index) => (
                    <Box
                      key={index}
                      component="img"
                      src={imgUrl}
                      alt={`Postprocessed ${index}`}
                      sx={{
                        width: 'calc(50% - 8px)', // Two images per row
                        height: 'auto',
                        borderRadius: 1,
                        boxShadow: 1,
                        backgroundColor: '#f5f5f5' // Background in case of transparency
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>

          {/* Purge CDN Section */}
          <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom color="warning.main">
              Purge CDN Cache
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Clear cached CDN content for specific product IDs (Max 70 items).
            </Typography>

            <TextField
              label="Product IDs (comma separated)"
              type="text"
              value={purgeProductIds}
              onChange={(e) => setPurgeProductIds(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="101, 102, 103"
            />

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="warning"
                fullWidth
                onClick={handlePurgeCdn}
                disabled={isPurging || !purgeProductIds.trim()}
                startIcon={isPurging ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isPurging ? 'Purging...' : 'Purge Cache'}
              </Button>
            </Box>

            {purgeStatus && (
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  fontWeight: 'bold',
                  color: purgeStatus.includes('Error') ? 'error.main' : 'success.main'
                }}
              >
                {purgeStatus}
              </Typography>
            )}
          </Paper>

          {/* Lock VTO / POM */}
          <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom color="secondary.main">
              Lock VTO / POM
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Set or remove the VTO/POM render lock for the given product IDs.
            </Typography>

            <TextField
              label="Product IDs (comma separated)"
              type="text"
              value={lockProductIds}
              onChange={(e) => setLockProductIds(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="101, 102, 103"
            />

            <FormGroup row sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lockVto}
                    onChange={(e) => setLockVto(e.target.checked)}
                  />
                }
                label="lock vto"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lockPom}
                    onChange={(e) => setLockPom(e.target.checked)}
                  />
                }
                label="lock pom"
              />
            </FormGroup>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                onClick={handleLockSubmit}
                disabled={isLocking || !lockProductIds.trim()}
                startIcon={isLocking ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isLocking ? 'Submitting...' : 'Apply Lock Settings'}
              </Button>
            </Box>

            {lockError && (
              <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
                {lockError}
              </Typography>
            )}

            {lockResults && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Results:
                </Typography>
                {Object.entries(lockResults.results || {}).map(([productId, statuses]) => {
                  const hasError = Object.values(statuses).includes('error');
                  return (
                    <Box
                      key={productId}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 0.5,
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                      }}
                    >
                      <Typography variant="body2">{productId}</Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: hasError ? 'error.main' : 'success.main' }}
                      >
                        vto: {statuses.vto} | pom: {statuses.pom}
                      </Typography>
                    </Box>
                  );
                })}

                {lockResults.failed_product_ids && lockResults.failed_product_ids.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="error.main" gutterBottom>
                      Failed product IDs (copy list below):
                    </Typography>
                    <TextField
                      value={lockResults.failed_product_ids.join(', ')}
                      fullWidth
                      multiline
                      InputProps={{ readOnly: true }}
                      onFocus={(e) => e.target.select()}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Paper>

        </Container>
      </ContentContainer>

      {/* ДИАЛОГОВОЕ ОКНО ПОДТВЕРЖДЕНИЯ */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Action confirmation"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to run the image regeneration process for the following Product IDs:
            <strong> {imgGenProductIds}</strong>?
            <br /><br />
            This action will send a request to the server.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleImageGeneration} color="secondary" variant="contained" autoFocus>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default RerenderPage;
